"use client";

import Link from "next/link";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { usePageTitle } from "@/lib/usePageTitle";
import { UsersIcon, OnlineIcon, ServerIcon, TrendingIcon, ArrowRightIcon } from "@/components/icons";
import {
  getServerLimitForSubscription,
  type SubscriptionTier,
  type SubscriptionStatus,
} from "@/lib/billing/plans";

function tierLabel(tier: SubscriptionTier) {
  switch (tier) {
    case "starter": return "Founder Starter";
    case "plus": return "Founder Plus";
    case "pro": return "Founder Pro";
    case "enterprise": return "Enterprise";
    default: return "Free";
  }
}

export default function PriorityQueuePage() {
  usePageTitle("Priority Queue");

  const ownedOrgs = useQuery(api.organizations.getOwned);
  const billingSummary = useQuery(api.billing.getMySummary);

  const tier = (billingSummary?.tier ?? "free") as SubscriptionTier;
  const status = (billingSummary?.status ?? "none") as SubscriptionStatus;
  const serverLimit = getServerLimitForSubscription(tier, status);
  const isActiveSubscriber = billingSummary !== undefined && billingSummary !== null &&
    (billingSummary.status === "active" || billingSummary.status === "trialing");

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Priority Queue</h1>
        <p className="mt-1 text-muted">
          Manage priority queue slots for your subscribed server networks.
        </p>
      </div>

      {/* Subscription Tier Banner */}
      {billingSummary !== undefined && billingSummary !== null && (
        <div className="mb-6 rounded-lg border border-border bg-foreground/[0.02] p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-primary/10 p-2 text-primary">
                <TrendingIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {tierLabel(tier)} Plan
                </p>
                <p className="text-xs text-muted capitalize">
                  {billingSummary.status === "trialing" ? "Free trial active" : billingSummary.status}
                  {billingSummary.cancelAtPeriodEnd && " · Cancels at period end"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-semibold text-foreground">
                  {serverLimit > 0
                    ? `${billingSummary.usage.activeOrgs} / ${serverLimit} servers`
                    : "No servers"}
                </p>
                <p className="text-xs text-muted">priority queue enabled</p>
              </div>
              <Link href="/dashboard/settings/billing">
                <Button variant="outline" size="sm">
                  Manage Plan
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Priority Queue Info */}
      <div className="mb-8 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              What is Priority Queue?
              <InfoHint text="Priority queue gives your subscribers faster server join times." />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted">
              Priority Queue is a Hytale server feature that moves your organization&apos;s
              subscribers to the front of the server join queue, reducing wait times during
              peak periods.
            </p>
            <ul className="space-y-2 text-sm">
              {[
                "Subscribers skip the general join queue",
                "Managed via the BlockyNetwork plugin on your server",
                "Synced in real-time via Convex heartbeat events",
                "Configurable per-organization",
              ].map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-muted">
                  <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-primary/15 flex items-center justify-center">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              Setup Requirements
              <InfoHint text="Steps needed to enable priority queue on your server." />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              {
                step: 1,
                label: "Install the BlockyNetwork plugin",
                done: true,
                link: "/downloads",
              },
              {
                step: 2,
                label: "Link your server to an organization",
                done: (ownedOrgs?.some((o) => o.serverId)) ?? false,
                link: "/dashboard/my-orgs",
              },
              {
                step: 3,
                label: "Configure plugin to enable priority join",
                done: false,
                link: "/docs",
              },
            ].map(({ step, label, done, link }) => (
              <div key={step} className="flex items-center gap-3">
                <div
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    done
                      ? "bg-primary text-primary-foreground"
                      : "bg-foreground/10 text-muted"
                  }`}
                >
                  {done ? "✓" : step}
                </div>
                <span className={`flex-1 text-sm ${done ? "text-foreground" : "text-muted"}`}>
                  {label}
                </span>
                <Link href={link} className="shrink-0">
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                    <ArrowRightIcon className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Per-Organization Queue Status */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          Organization Queue Status
          <InfoHint text="Priority queue status for each of your owned organizations." />
        </h2>

        {ownedOrgs === undefined ? (
          <div className="space-y-3">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        ) : ownedOrgs.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <ServerIcon className="mx-auto h-10 w-10 text-muted" />
              <p className="mt-3 text-sm text-muted">
                No organizations yet.{" "}
                <Link href="/dashboard/my-orgs/new" className="text-primary hover:underline">
                  Create one
                </Link>{" "}
                to enable priority queue for your server.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {ownedOrgs.map((org) => (
              <OrgQueueRow
                key={org._id}
                org={org}
                isActiveSubscriber={isActiveSubscriber}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OrgQueueRow({
  org,
  isActiveSubscriber,
}: {
  org: {
    _id: string;
    name: string;
    slug: string;
    serverId?: string;
    serverIp?: string;
    memberCount: number;
    onlineCount: number;
    status?: "online" | "offline";
  };
  isActiveSubscriber: boolean;
}) {
  const isLinked = !!org.serverId;
  const isOnline = org.status === "online";
  const queueEnabled = isLinked && isActiveSubscriber;

  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Link
              href={`/org/${org.slug}`}
              className="font-semibold text-foreground hover:text-primary transition-colors"
            >
              {org.name}
            </Link>
            <Badge
              variant={isOnline ? "success" : "outline"}
              className="text-xs"
            >
              <OnlineIcon className="mr-1 h-2.5 w-2.5" />
              {isOnline ? "Online" : "Offline"}
            </Badge>
          </div>
          {org.serverIp && (
            <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted">
              <ServerIcon className="h-3.5 w-3.5" />
              <code>{org.serverIp}</code>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-muted">
            <UsersIcon className="h-4 w-4" />
            <span>{org.memberCount} members</span>
          </div>

          <div>
            {!isLinked ? (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 dark:border-amber-700 dark:text-amber-400">
                Plugin not linked
              </Badge>
            ) : queueEnabled ? (
              <Badge variant="success" className="text-xs">
                Priority queue active
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs">
                Inactive
              </Badge>
            )}
          </div>

          <Link href={`/org/${org.slug}/settings`}>
            <Button variant="outline" size="sm" className="h-8 text-xs">
              Configure
              <ArrowRightIcon className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
